import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Tag, Plus, Calendar, Percent, Hash } from 'lucide-react';
import { toast } from 'sonner';

const Coupons = () => {
  const { fetchWithAuth, isSuperAdmin, isSalesManager } = usePlatformAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'PERCENT',
    discount_value: 10,
    discount_duration_months: 3,
    max_uses: ''
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth('/api/platform/coupons');
      if (!response.ok) throw new Error('Failed to load coupons');
      
      const data = await response.json();
      setCoupons(data.coupons || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.code) {
      toast.error('Please enter a coupon code');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...formData,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null
      };

      const response = await fetchWithAuth('/api/platform/coupons', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create coupon');
      }

      toast.success('Coupon created successfully');
      setShowCreateDialog(false);
      setFormData({
        code: '',
        discount_type: 'PERCENT',
        discount_value: 10,
        discount_duration_months: 3,
        max_uses: ''
      });
      loadCoupons();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <PlatformLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Coupons</h1>
            <p className="text-slate-400 mt-1">Manage discount codes and promotions</p>
          </div>
          
          {(isSuperAdmin || isSalesManager) && (
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-coupon"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Coupon
            </Button>
          )}
        </div>

        {/* Coupons List */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : coupons.length === 0 ? (
              <div className="text-center py-12">
                <Tag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No coupons created yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-slate-700">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Code</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Discount</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Duration</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Usage</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Expires</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {coupons.map((coupon) => (
                      <tr key={coupon.coupon_id} className="hover:bg-slate-700/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Tag className="w-4 h-4 text-purple-400" />
                            <span className="font-mono font-medium text-white">{coupon.code}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge className="bg-green-500/10 text-green-400">
                            {coupon.discount_type === 'PERCENT' 
                              ? `${coupon.discount_value}%`
                              : `₹${coupon.discount_value / 100}`
                            }
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-slate-300">{coupon.discount_duration_months} months</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-slate-300">
                            {coupon.times_used}
                            {coupon.max_uses ? ` / ${coupon.max_uses}` : ' / ∞'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-slate-400 text-sm">
                            {formatDate(coupon.valid_until)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <Badge className={coupon.is_active 
                            ? 'bg-green-500/10 text-green-400' 
                            : 'bg-slate-600 text-slate-300'
                          }>
                            {coupon.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Coupon Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Create Coupon</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new promotional discount code
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Coupon Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="WELCOME20"
                className="bg-slate-700/50 border-slate-600 text-white font-mono"
                data-testid="coupon-code"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Discount Type</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="PERCENT" className="text-white hover:bg-slate-700">
                      Percentage
                    </SelectItem>
                    <SelectItem value="FLAT" className="text-white hover:bg-slate-700">
                      Flat Amount
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">
                  {formData.discount_type === 'PERCENT' ? 'Discount %' : 'Amount (₹)'}
                </Label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  data-testid="discount-value"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Duration (months)</Label>
                <Input
                  type="number"
                  value={formData.discount_duration_months}
                  onChange={(e) => setFormData({ ...formData, discount_duration_months: parseInt(e.target.value) })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Max Uses</Label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-coupon"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Coupon'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PlatformLayout>
  );
};

export default Coupons;
